import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/Layout/AdminLayout';
import {
    FileText,
    Plus,
    Edit3,
    Save,
    X,
    Clock,
    Target,
    CheckCircle,
    Settings,
    Eye,
    EyeOff,
    History
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AssessmentManagement = () => {
    const [assessment, setAssessment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editedAssessment, setEditedAssessment] = useState(null);
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [versions, setVersions] = useState([]);
    const [changeNotes, setChangeNotes] = useState('');

    useEffect(() => {
        fetchAssessment();
        if (showVersionHistory) {
            fetchVersionHistory();
        }
    }, [showVersionHistory]);

    const fetchAssessment = async () => {
        try {
            const response = await axios.get('/api/assessment');
            setAssessment(response.data);
            setEditedAssessment(JSON.parse(JSON.stringify(response.data))); // Deep clone
        } catch (error) {
            toast.error('Failed to load assessment');
            console.error('Assessment fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchVersionHistory = async () => {
        try {
            const response = await axios.get('/api/assessment/versions');
            setVersions(response.data);
        } catch (error) {
            toast.error('Failed to load version history');
            console.error('Version history fetch error:', error);
        }
    };

    const handleSave = async () => {
        setSaving(true);

        try {
            const response = await axios.put('/api/assessment', {
                ...editedAssessment,
                changeNotes: changeNotes
            });
            setAssessment(response.data);
            setEditedAssessment(JSON.parse(JSON.stringify(response.data)));
            setEditing(false);
            setChangeNotes('');
            toast.success('Assessment updated successfully - New version created');
        } catch (error) {
            toast.error('Failed to update assessment');
            console.error('Assessment update error:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditedAssessment(JSON.parse(JSON.stringify(assessment)));
        setEditing(false);
    };

    const updateBasicInfo = (field, value) => {
        setEditedAssessment(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const updateSection = (index, field, value) => {
        setEditedAssessment(prev => ({
            ...prev,
            sections: prev.sections.map((section, i) =>
                i === index ? { ...section, [field]: value } : section
            )
        }));
    };

    const updateQuestion = (index, field, value) => {
        setEditedAssessment(prev => ({
            ...prev,
            questions: prev.questions.map((question, i) =>
                i === index ? { ...question, [field]: value } : question
            )
        }));
    };

    const addQuestion = () => {
        const newQuestion = {
            id: `q${Date.now()}`,
            type: 'yes_no',
            question: 'New question',
            correctAnswer: 'yes',
            points: 1,
            section: 1,
            required: true
        };

        setEditedAssessment(prev => ({
            ...prev,
            questions: [...prev.questions, newQuestion]
        }));
    };

    const removeQuestion = (index) => {
        setEditedAssessment(prev => ({
            ...prev,
            questions: prev.questions.filter((_, i) => i !== index)
        }));
    };

    const addOption = (questionIndex) => {
        const newOption = {
            id: String.fromCharCode(97 + Math.random() * 26),
            text: 'New option'
        };

        setEditedAssessment(prev => ({
            ...prev,
            questions: prev.questions.map((question, i) =>
                i === questionIndex
                    ? { ...question, options: [...(question.options || []), newOption] }
                    : question
            )
        }));
    };

    const updateOption = (questionIndex, optionIndex, field, value) => {
        setEditedAssessment(prev => ({
            ...prev,
            questions: prev.questions.map((question, i) =>
                i === questionIndex
                    ? {
                        ...question,
                        options: question.options.map((option, j) =>
                            j === optionIndex ? { ...option, [field]: value } : option
                        )
                    }
                    : question
            )
        }));
    };

    const removeOption = (questionIndex, optionIndex) => {
        setEditedAssessment(prev => ({
            ...prev,
            questions: prev.questions.map((question, i) =>
                i === questionIndex
                    ? { ...question, options: question.options.filter((_, j) => j !== optionIndex) }
                    : question
            )
        }));
    };

    const handleFileUpload = async (event, questionIndex) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post('/api/assessment/upload-file', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            updateQuestion(questionIndex, 'downloadableFile', response.data.file);
        } catch (error) {
            console.error('File upload error:', error);
            toast.error('File upload failed');
        }
    };

    const handleRemoveFile = async (questionIndex) => {
        const question = editedAssessment.questions[questionIndex];
        if (question.downloadableFile?.filename) {
            try {
                await axios.delete(`/api/assessment/file/${question.downloadableFile.filename}`);
            } catch (error) {
                console.error('File deletion error:', error);
            }
        }
        updateQuestion(questionIndex, 'downloadableFile', null);
    };

    const getQuestionTypeLabel = (type) => {
        switch (type) {
            case 'yes_no': return 'Yes/No';
            case 'multiple_choice': return 'Multiple Choice';
            case 'file_upload': return 'File Upload';
            default: return type;
        }
    };

    const currentData = editing ? editedAssessment : assessment;

    if (loading) {
        return (
            <AdminLayout title="Assessment Management">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="loading-spinner"></div>
                </div>
            </AdminLayout>
        );
    }

    if (!currentData) {
        return (
            <AdminLayout title="Assessment Management">
                <div className="card">
                    <div className="empty-state">
                        <FileText size={48} className="text-muted" />
                        <h3>No Assessment Found</h3>
                        <p>No assessment configuration found. Please contact system administrator.</p>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Assessment Management">
            <div className="assessment-management">
                {/* Header */}
                <div className="page-header">
                    <div className="header-info">
                        <h1>Assessment Configuration</h1>
                        <p>Configure assessment settings, sections, and questions</p>
                    </div>

                    <div className="header-actions">
                        {editing ? (
                            <>
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleCancel}
                                    disabled={saving}
                                >
                                    <X size={20} />
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <div className="loading-spinner" style={{ width: '16px', height: '16px' }} />
                                    ) : (
                                        <Save size={20} />
                                    )}
                                    Save Changes
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn btn-outline"
                                    onClick={() => setShowVersionHistory(!showVersionHistory)}
                                >
                                    <History size={20} />
                                    Version History
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setEditing(true)}
                                >
                                    <Edit3 size={20} />
                                    Edit Assessment
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Version History Modal */}
                {showVersionHistory && (
                    <motion.div
                        className="card"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                    >
                        <div className="card-header">
                            <h3 className="card-title">Version History</h3>
                            <p className="card-subtitle">Track changes and maintain assessment integrity</p>
                        </div>
                        <div className="card-content">
                            {versions.length === 0 ? (
                                <div className="empty-state">
                                    <History size={32} className="text-muted" />
                                    <p>No version history available</p>
                                </div>
                            ) : (
                                <div className="version-list">
                                    {versions.map((version, index) => (
                                        <div key={version._id} className="version-item">
                                            <div className="version-info">
                                                <div className="version-header">
                                                    <span className="version-number">v{version.version}</span>
                                                    {version.isActive && (
                                                        <span className="version-badge active">Current</span>
                                                    )}
                                                    <span className="version-date">
                                                        {new Date(version.createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <p className="version-notes">
                                                    {version.changeNotes || 'No change notes provided'}
                                                </p>
                                                <div className="version-meta">
                                                    <span>By: {version.createdBy?.name || 'System'}</span>
                                                    <span>{version.sections?.length || 0} sections</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* Change Notes Input (when editing) */}
                {editing && (
                    <motion.div
                        className="card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="card-header">
                            <h3 className="card-title">Change Notes</h3>
                            <p className="card-subtitle">Document what's being changed (optional)</p>
                        </div>
                        <div className="card-content">
                            <div className="form-group">
                                <textarea
                                    className="form-control"
                                    placeholder="Describe the changes you're making..."
                                    value={changeNotes}
                                    onChange={(e) => setChangeNotes(e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Basic Settings */}
                <motion.div
                    className="card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className="card-header">
                        <h2 className="card-title">
                            <Settings size={20} />
                            Basic Settings
                        </h2>
                    </div>

                    <div className="settings-grid">
                        <div className="form-group">
                            <label className="form-label">Assessment Title</label>
                            {editing ? (
                                <input
                                    type="text"
                                    className="form-control"
                                    value={currentData.title}
                                    onChange={(e) => updateBasicInfo('title', e.target.value)}
                                />
                            ) : (
                                <div className="readonly-value">{currentData.title}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">Description</label>
                            {editing ? (
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={currentData.description}
                                    onChange={(e) => updateBasicInfo('description', e.target.value)}
                                />
                            ) : (
                                <div className="readonly-value">{currentData.description}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <Clock size={16} />
                                Time Limit (minutes)
                            </label>
                            {editing ? (
                                <input
                                    type="number"
                                    className="form-control"
                                    value={currentData.timeLimit}
                                    onChange={(e) => updateBasicInfo('timeLimit', parseInt(e.target.value))}
                                    min="1"
                                />
                            ) : (
                                <div className="readonly-value">{currentData.timeLimit} minutes</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <Target size={16} />
                                Passing Score (%)
                            </label>
                            {editing ? (
                                <input
                                    type="number"
                                    className="form-control"
                                    value={currentData.passingScore}
                                    onChange={(e) => updateBasicInfo('passingScore', parseInt(e.target.value))}
                                    min="0"
                                    max="100"
                                />
                            ) : (
                                <div className="readonly-value">{currentData.passingScore}%</div>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* Sections */}
                <motion.div
                    className="card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                >
                    <div className="card-header">
                        <h2 className="card-title">Assessment Sections</h2>
                    </div>

                    <div className="sections-list">
                        {currentData.sections?.map((section, index) => (
                            <div key={section.id} className="section-item">
                                <div className="section-header">
                                    <h4>Section {section.id}</h4>
                                    <span className="question-count">
                                        {currentData.questions?.filter(q => q.section === section.id).length || 0} questions
                                    </span>
                                </div>

                                <div className="section-content">
                                    <div className="form-group">
                                        <label className="form-label">Title</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={section.title}
                                                onChange={(e) => updateSection(index, 'title', e.target.value)}
                                            />
                                        ) : (
                                            <div className="readonly-value">{section.title}</div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Description</label>
                                        {editing ? (
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                value={section.description}
                                                onChange={(e) => updateSection(index, 'description', e.target.value)}
                                            />
                                        ) : (
                                            <div className="readonly-value">{section.description}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Questions */}
                <motion.div
                    className="card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                >
                    <div className="card-header">
                        <h2 className="card-title">Questions ({currentData.questions?.length || 0})</h2>
                        {editing && (
                            <button className="btn btn-primary btn-sm" onClick={addQuestion}>
                                <Plus size={16} />
                                Add Question
                            </button>
                        )}
                    </div>

                    <div className="questions-list">
                        {currentData.questions?.map((question, index) => (
                            <div key={question.id} className="question-item">
                                <div className="question-header">
                                    <div className="question-meta">
                                        <span className="question-number">Q{index + 1}</span>
                                        <span className="question-type">{getQuestionTypeLabel(question.type)}</span>
                                        <span className="question-section">Section {question.section}</span>
                                        <span className="question-points">{question.points} pts</span>
                                    </div>

                                    {editing && (
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => removeQuestion(index)}
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>

                                <div className="question-content">
                                    <div className="form-group">
                                        <label className="form-label">Question Text</label>
                                        {editing ? (
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                value={question.question}
                                                onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                                            />
                                        ) : (
                                            <div className="readonly-value">{question.question}</div>
                                        )}
                                    </div>

                                    <div className="question-settings">
                                        <div className="form-group">
                                            <label className="form-label">Type</label>
                                            {editing ? (
                                                <select
                                                    className="form-control"
                                                    value={question.type}
                                                    onChange={(e) => updateQuestion(index, 'type', e.target.value)}
                                                >
                                                    <option value="yes_no">Yes/No</option>
                                                    <option value="multiple_choice">Multiple Choice</option>
                                                    <option value="file_upload">File Upload</option>
                                                </select>
                                            ) : (
                                                <div className="readonly-value">{getQuestionTypeLabel(question.type)}</div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Section</label>
                                            {editing ? (
                                                <select
                                                    className="form-control"
                                                    value={question.section}
                                                    onChange={(e) => updateQuestion(index, 'section', parseInt(e.target.value))}
                                                >
                                                    {currentData.sections?.map(section => (
                                                        <option key={section.id} value={section.id}>
                                                            Section {section.id}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className="readonly-value">Section {question.section}</div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Points</label>
                                            {editing ? (
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={question.points}
                                                    onChange={(e) => updateQuestion(index, 'points', parseInt(e.target.value))}
                                                    min="1"
                                                />
                                            ) : (
                                                <div className="readonly-value">{question.points}</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Options for multiple choice */}
                                    {question.type === 'multiple_choice' && (
                                        <div className="question-options">
                                            <div className="options-header">
                                                <label className="form-label">Answer Options</label>
                                                {editing && (
                                                    <button
                                                        className="btn btn-sm btn-outline"
                                                        onClick={() => addOption(index)}
                                                    >
                                                        <Plus size={14} />
                                                        Add Option
                                                    </button>
                                                )}
                                            </div>

                                            <div className="options-list">
                                                {question.options?.map((option, optionIndex) => (
                                                    <div key={option.id} className="option-item">
                                                        <div className="option-content">
                                                            <span className="option-id">{option.id.toUpperCase()}</span>
                                                            {editing ? (
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={option.text}
                                                                    onChange={(e) => updateOption(index, optionIndex, 'text', e.target.value)}
                                                                />
                                                            ) : (
                                                                <span>{option.text}</span>
                                                            )}

                                                            {question.correctAnswer === option.id && (
                                                                <CheckCircle size={16} className="text-success" title="Correct Answer" />
                                                            )}
                                                        </div>

                                                        {editing && (
                                                            <div className="option-actions">
                                                                <label className="correct-answer">
                                                                    <input
                                                                        type="radio"
                                                                        name={`correct-${question.id}`}
                                                                        checked={question.correctAnswer === option.id}
                                                                        onChange={() => updateQuestion(index, 'correctAnswer', option.id)}
                                                                    />
                                                                    Correct
                                                                </label>
                                                                <button
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => removeOption(index, optionIndex)}
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Correct answer for yes/no */}
                                    {question.type === 'yes_no' && (
                                        <div className="form-group">
                                            <label className="form-label">Correct Answer</label>
                                            {editing ? (
                                                <select
                                                    className="form-control"
                                                    value={question.correctAnswer}
                                                    onChange={(e) => updateQuestion(index, 'correctAnswer', e.target.value)}
                                                >
                                                    <option value="yes">Yes</option>
                                                    <option value="no">No</option>
                                                </select>
                                            ) : (
                                                <div className="readonly-value">
                                                    {question.correctAnswer === 'yes' ? 'Yes' : 'No'}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* File instructions */}
                                    {question.type === 'file_upload' && (
                                        <div className="form-group">
                                            <label className="form-label">File Instructions</label>
                                            {editing ? (
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    value={question.fileInstructions || ''}
                                                    onChange={(e) => updateQuestion(index, 'fileInstructions', e.target.value)}
                                                />
                                            ) : (
                                                <div className="readonly-value">{question.fileInstructions || 'No instructions provided'}</div>
                                            )}
                                        </div>
                                    )}

                                    {/* Downloadable file for file upload questions */}
                                    {question.type === 'file_upload' && (
                                        <div className="form-group">
                                            <label className="form-label">
                                                <input
                                                    type="checkbox"
                                                    checked={question.hasDownloadableFile || false}
                                                    onChange={(e) => {
                                                        updateQuestion(index, 'hasDownloadableFile', e.target.checked);
                                                        if (!e.target.checked) {
                                                            updateQuestion(index, 'downloadableFile', null);
                                                        }
                                                    }}
                                                    disabled={!editing}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                Provide downloadable file for this question
                                            </label>

                                            {question.hasDownloadableFile && editing && (
                                                <div className="file-upload-section" style={{ marginTop: '12px' }}>
                                                    <input
                                                        type="file"
                                                        accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.zip"
                                                        onChange={(e) => handleFileUpload(e, index)}
                                                        style={{ marginBottom: '8px' }}
                                                    />
                                                    {question.downloadableFile && (
                                                        <div className="uploaded-file-info" style={{
                                                            padding: '8px',
                                                            background: '#f8f9fa',
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <span><strong>{question.downloadableFile.originalName}</strong></span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => handleRemoveFile(index)}
                                                                    style={{ padding: '2px 8px' }}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                            <small style={{ color: '#6c757d' }}>
                                                                {(question.downloadableFile.size / 1024).toFixed(1)} KB
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {question.hasDownloadableFile && !editing && question.downloadableFile && (
                                                <div className="readonly-file-info" style={{
                                                    marginTop: '8px',
                                                    padding: '8px',
                                                    background: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #dee2e6'
                                                }}>
                                                    <strong>{question.downloadableFile.originalName}</strong>
                                                    <br />
                                                    <small style={{ color: '#6c757d' }}>
                                                        {(question.downloadableFile.size / 1024).toFixed(1)} KB
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>

            <style jsx>{`
        .assessment-management {
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          gap: 24px;
        }

        .header-info h1 {
          margin: 0 0 8px 0;
          color: var(--primary-accent);
          font-size: 32px;
        }

        .header-info p {
          margin: 0;
          color: var(--text-secondary);
          font-size: 16px;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .settings-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
          padding: 24px;
        }

        .readonly-value {
          padding: 12px 16px;
          background-color: var(--gray-100);
          border-radius: var(--border-radius);
          border: 2px solid transparent;
          min-height: 48px;
          display: flex;
          align-items: center;
        }

        .sections-list {
          padding: 0 24px 24px 24px;
        }

        .section-item {
          background-color: var(--gray-50);
          border-radius: var(--border-radius);
          padding: 20px;
          margin-bottom: 16px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .section-header h4 {
          margin: 0;
          color: var(--primary-accent);
          font-size: 18px;
        }

        .question-count {
          background-color: var(--primary-accent);
          color: var(--white);
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .section-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
        }

        .questions-list {
          padding: 0 24px 24px 24px;
        }

        .question-item {
          background-color: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--border-radius-lg);
          padding: 24px;
          margin-bottom: 24px;
        }

        .question-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .question-meta {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .question-number {
          background-color: var(--primary-accent);
          color: var(--white);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }

        .question-type {
          background-color: var(--info);
          color: var(--white);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .question-section {
          background-color: var(--success);
          color: var(--white);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .question-points {
          background-color: var(--warning);
          color: var(--white);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .question-settings {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-top: 16px;
        }

        .options-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 16px 0 12px 0;
        }

        .options-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .option-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background-color: var(--gray-50);
          border-radius: var(--border-radius);
          gap: 12px;
        }

        .option-content {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .option-id {
          background-color: var(--primary-accent);
          color: var(--white);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          min-width: 24px;
          text-align: center;
        }

        .option-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .correct-answer {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: var(--success);
          cursor: pointer;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: var(--text-primary);
        }

        .empty-state p {
          color: var(--text-secondary);
          margin: 0;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .settings-grid {
            grid-template-columns: 1fr;
          }

          .section-content {
            grid-template-columns: 1fr;
          }

          .question-settings {
            grid-template-columns: 1fr;
          }

          .question-meta {
            flex-wrap: wrap;
          }

          .option-item {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }

          .option-actions {
            justify-content: space-between;
          }
        }

        /* Version History Styles */
        .version-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .version-item {
          background: var(--background);
          border: 1px solid var(--border);
          border-radius: var(--border-radius);
          padding: 20px;
          transition: var(--transition);
        }

        .version-item:hover {
          border-color: var(--primary);
          box-shadow: var(--shadow-sm);
        }

        .version-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .version-number {
          background: var(--primary);
          color: white;
          padding: 4px 12px;
          border-radius: var(--border-radius);
          font-weight: 600;
          font-size: 14px;
        }

        .version-badge {
          padding: 4px 8px;
          border-radius: var(--border-radius);
          font-size: 12px;
          font-weight: 500;
        }

        .version-badge.active {
          background: var(--success);
          color: white;
        }

        .version-date {
          color: var(--muted-foreground);
          font-size: 14px;
        }

        .version-notes {
          color: var(--foreground);
          margin: 12px 0;
          font-style: italic;
        }

        .version-meta {
          display: flex;
          gap: 16px;
          font-size: 14px;
          color: var(--muted-foreground);
        }

        .header-actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }
      `}</style>
        </AdminLayout>
    );
};

export default AssessmentManagement;
